/**
 * The passkey (Face ID / Touch ID) enrolment round-trip, at the transport level
 * (user-story-merchant-portal-060, adr-merchant-portal-020, plan-platform-001 step 3.1/3.3).
 *
 * Enrolment is Keycloak's Application Initiated Action `kc_action=webauthn-register-passwordless` — a
 * **full-page** OIDC round trip through `web-merchant-portal-bff`'s authorize endpoint, exactly like
 * login and the step-up challenge (`shared/api/stepUp.ts`). This module is the sibling of `stepUp.ts`:
 * same `sessionStorage` marker discipline, same TTL rationale, same guarded (never-throwing) storage
 * access, same consume-once/path-matched accessor. Unlike a step-up, this trip is always started by a
 * deliberate user click (the card's primary button, or the nudge's "Настроить"), never by an
 * interceptor reacting to a failed request — so there is no loop to break and no cooldown here.
 *
 * ## What the round trip has to survive
 *
 * 1. **Start** (`startPasskeyEnrolment`) writes the marker and the shared re-auth return path, then
 *    navigates. The document is about to unload, so nothing here needs to reset itself.
 * 2. **Boot after the trip**: `captureEnrolmentStatusFromLocation()` runs once at SPA boot (see `App`),
 *    strictly before `BffSessionProvider`'s return-path effect can `location.replace` the boot URL's
 *    query away — capturing Keycloak's `kc_action_status` (when the BFF forwards it; A3 in the design
 *    doc) onto the marker before that information is lost.
 * 3. **Land**: `BffSessionProvider` restores the return path exactly as it does after a 401/step-up —
 *    unmodified by this feature.
 * 4. **Consume**: `takePasskeyEnrolmentOutcome` — the page itself when the trip returns to
 *    `/account/security`, otherwise `PasskeyEnrolmentReturn` (mounted in `AppLayout`).
 * 5. **Resolve**: `resolvePasskeyEnrolmentOutcome` turns the marker + the fresh session hint into one of
 *    four user-facing outcomes.
 */

import { REAUTH_REDIRECT, REAUTH_RETURN_KEY, safeReturnPath } from '@/shared/api';
import { env } from '@/shared/config/env';

/** The Keycloak Application Initiated Action this feature registers for. */
export const PASSKEY_REGISTER_ACTION = 'webauthn-register-passwordless';

/** Where the interrupted enrolment is parked across the Keycloak round-trip. */
export const PASSKEY_ENROLMENT_PENDING_KEY = 'obzr:auth:passkey_enrolment';

/**
 * How long a started enrolment stays consumable — same rationale as `STEP_UP_PENDING_TTL_MS`: generous
 * enough for a registration that needs a fresh sign-in first, short enough that a tab resumed from
 * sleep tomorrow does not surface a stale outcome the user has forgotten about.
 */
export const PASSKEY_ENROLMENT_TTL_MS = 15 * 60_000;

/**
 * `localStorage` key recording that the post-login nudge has been answered on this browser profile —
 * deliberately `localStorage`, not `sessionStorage`: "once per device" must survive the tab closing,
 * unlike the enrolment marker above, which only has to survive one navigation.
 */
export const PASSKEY_NUDGE_KEY = 'obzr.merchantPortal.passkeyNudge';

/** Keycloak's `kc_action_status` values for a WebAuthn registration AIA. */
export type PasskeyActionStatus = 'success' | 'cancelled' | 'error';

/** The four outcomes the UI can show after a round trip — see {@link resolvePasskeyEnrolmentOutcome}. */
export type PasskeyEnrolmentOutcome = 'success' | 'cancelled' | 'error' | 'unknown';

/** The interrupted enrolment, in a form that survives `sessionStorage` and a full page load. */
export interface PendingPasskeyEnrolment {
  /** Epoch ms — see {@link PASSKEY_ENROLMENT_TTL_MS}. */
  startedAt: number;
  /** Where the CTA was clicked — informational only; both sources resolve the same way. */
  source: 'card' | 'nudge';
  /** SPA path to come back to, or `null` when there was nothing worth restoring (see `safeReturnPath`). */
  returnPath: string | null;
  /** The session's `passkeyEnrolled` hint at the moment enrolment started — `true` when this is "add
   *  another device" rather than a first enrolment, so a hint that was already `true` cannot be
   *  (mis)read as evidence the round trip just succeeded. */
  enrolledBefore: boolean | null;
  /** Keycloak's outcome, captured from the boot URL by {@link captureEnrolmentStatusFromLocation} —
   *  absent until the BFF forwards `kc_action_status` (A3) and the boot URL carries it. */
  status?: PasskeyActionStatus;
}

/** Best-effort `sessionStorage` read — never throws (privacy modes, enterprise policy block it outright). */
function readSession(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

/** Best-effort `sessionStorage` write — see {@link readSession}. */
function writeSession(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // Storage blocked: the round trip still happens, it simply cannot report an outcome afterwards.
  }
}

function removeSession(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // Nothing was stored either, so there is nothing to clear.
  }
}

function readPendingPasskeyEnrolment(): PendingPasskeyEnrolment | null {
  const raw = readSession(PASSKEY_ENROLMENT_PENDING_KEY);
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as PendingPasskeyEnrolment;
    if (typeof parsed?.startedAt !== 'number' || typeof parsed?.source !== 'string') {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writePendingPasskeyEnrolment(pending: PendingPasskeyEnrolment): void {
  try {
    writeSession(PASSKEY_ENROLMENT_PENDING_KEY, JSON.stringify(pending));
  } catch {
    // A value we could not stringify would already have failed upstream; belt and braces.
  }
}

function clearPendingPasskeyEnrolment(): void {
  removeSession(PASSKEY_ENROLMENT_PENDING_KEY);
}

/**
 * The BFF authorize URL carrying the enrolment action, optionally the belt-and-braces
 * `:skip_if_exists` suffix the nudge uses (mirrors `stepUpAuthorizeUrl`'s shape).
 *
 * Not percent-encoded: `PASSKEY_REGISTER_ACTION` is a fixed, URL-safe literal this module controls
 * (unlike a step-up's server-supplied `requiredAcr`), and the BFF's allow-list compares the raw
 * `kc_action` value verbatim.
 */
export function passkeyEnrolmentUrl(
  authorizeUrl: string,
  opts?: { skipIfExists?: boolean },
): string {
  const separator = authorizeUrl.includes('?') ? '&' : '?';
  const action = opts?.skipIfExists
    ? `${PASSKEY_REGISTER_ACTION}:skip_if_exists`
    : PASSKEY_REGISTER_ACTION;
  return `${authorizeUrl}${separator}kc_action=${action}`;
}

/**
 * Starts enrolment: parks the marker, saves the return path on the same key the 401/step-up round
 * trips restore from, then performs the full-page navigation.
 *
 * No cooldown / in-flight guard, unlike `stepUp.ts` — this is always a direct user click, never an
 * interceptor retrying a failed request, so there is no redirect loop to break. The caller (the card's
 * button, the nudge) is responsible for disabling itself; the navigation about to happen makes that
 * state never need resetting (in `bff` mode — see below).
 *
 * In `mock` auth mode this is a no-op, mirroring `MockSessionProvider.login` (`() => {}`): there is no
 * real BFF/Keycloak behind a local/offline session to redirect to, so sending it there would just
 * navigate the developer's browser to a dead URL instead of quietly doing nothing, the way the mock
 * login button already does (user-story-merchant-portal-060 AC14).
 */
export function startPasskeyEnrolment(opts: {
  source: 'card' | 'nudge';
  skipIfExists?: boolean;
  returnPath: string | null;
  enrolledBefore: boolean | null;
}): void {
  if (env.authMode !== 'bff') {
    return;
  }
  const returnPath = opts.returnPath ? safeReturnPath(opts.returnPath) : null;
  const pending: PendingPasskeyEnrolment = {
    startedAt: Date.now(),
    source: opts.source,
    returnPath,
    enrolledBefore: opts.enrolledBefore,
  };
  writePendingPasskeyEnrolment(pending);
  if (returnPath) {
    writeSession(REAUTH_RETURN_KEY, returnPath);
  }
  window.location.assign(passkeyEnrolmentUrl(REAUTH_REDIRECT, { skipIfExists: opts.skipIfExists }));
}

function isPasskeyActionStatus(value: string | null): value is PasskeyActionStatus {
  return value === 'success' || value === 'cancelled' || value === 'error';
}

/**
 * Captures Keycloak's `kc_action_status` from the boot URL onto the pending marker, then strips the
 * query parameter — must run once at SPA boot, before `BffSessionProvider`'s own return-path effect can
 * `location.replace` this query string away (see the module doc, step 2).
 *
 * A no-op whenever there is nothing to capture: no `kc_action_status` param, an unrecognised value, or
 * no pending marker to attach it to (someone landed on this URL by coincidence, not from this flow).
 * Idempotent — safe under StrictMode's double-invoked effects — because the first call already strips
 * the parameter the second call would otherwise act on.
 */
export function captureEnrolmentStatusFromLocation(): void {
  const params = new URLSearchParams(window.location.search);
  const status = params.get('kc_action_status');
  if (!isPasskeyActionStatus(status)) {
    return;
  }
  const pending = readPendingPasskeyEnrolment();
  if (!pending) {
    return;
  }
  writePendingPasskeyEnrolment({ ...pending, status });

  params.delete('kc_action_status');
  const query = params.toString();
  const nextUrl = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`;
  window.history.replaceState(window.history.state, '', nextUrl);
}

/**
 * Consume-once, path-matched accessor — mirrors `takePendingStepUp` exactly: discards a marker older
 * than {@link PASSKEY_ENROLMENT_TTL_MS}, returns `null` (without consuming) when the marker names a
 * specific return path that is not the current one, and otherwise removes the marker **before**
 * returning it so a reload can never repeat the outcome it describes.
 *
 * A `null` `returnPath` (the enrolment started somewhere `safeReturnPath` said was "nothing worth
 * restoring", e.g. the dashboard) matches any path — there was nowhere more specific to wait for.
 */
export function takePasskeyEnrolmentOutcome(currentPath: string): PendingPasskeyEnrolment | null {
  const pending = readPendingPasskeyEnrolment();
  if (!pending) {
    return null;
  }
  if (
    !Number.isFinite(pending.startedAt) ||
    Date.now() - pending.startedAt > PASSKEY_ENROLMENT_TTL_MS
  ) {
    clearPendingPasskeyEnrolment();
    return null;
  }
  if (pending.returnPath && pending.returnPath !== currentPath) {
    return null;
  }
  clearPendingPasskeyEnrolment();
  return pending;
}

/**
 * Non-destructive existence check for `PasskeyNudgeBanner`'s trigger: "no enrolment is pending" (the
 * user just came back from Keycloak and a consumer elsewhere still owns reporting that outcome). Unlike
 * {@link takePasskeyEnrolmentOutcome} this never removes the marker — the nudge only ever *peeks*, so it
 * cannot race the page or `PasskeyEnrolmentReturn` for the one consumption that must report the outcome.
 * A marker older than the TTL does not count as pending — it is functionally already gone.
 */
export function hasPendingPasskeyEnrolment(): boolean {
  const pending = readPendingPasskeyEnrolment();
  if (!pending) {
    return false;
  }
  return (
    Number.isFinite(pending.startedAt) && Date.now() - pending.startedAt <= PASSKEY_ENROLMENT_TTL_MS
  );
}

/**
 * Turns a consumed marker into one of four user-facing outcomes (design doc §11 step 5), in order:
 *
 * 1. Keycloak's own `kc_action_status`, when the BFF forwarded it (A3) — the truth, when available.
 * 2. Otherwise, a hint that flipped from not-confirmed-enrolled to confirmed-enrolled is treated as a
 *    `success` the BFF didn't need to say out loud. `enrolledBefore === true` (adding a further device)
 *    deliberately excludes this: the hint staying `true` is not evidence of anything.
 * 3. Otherwise `unknown` — truthful rather than guessed, per adr-merchant-portal-020 D3.
 */
export function resolvePasskeyEnrolmentOutcome(
  pending: PendingPasskeyEnrolment,
  freshPasskeyEnrolled: boolean | null,
): PasskeyEnrolmentOutcome {
  if (pending.status) {
    return pending.status;
  }
  if (pending.enrolledBefore !== true && freshPasskeyEnrolled === true) {
    return 'success';
  }
  return 'unknown';
}

/** Best-effort `localStorage` read — see {@link readSession}; `PasskeyNudgeBanner` treats a thrown
 *  read the same as "not answered" and relies on its own component state to stay dismissed for the
 *  current document once the user does answer (see `markNudgeAnswered`). */
export function readNudgeAnswered(): boolean {
  try {
    return localStorage.getItem(PASSKEY_NUDGE_KEY) !== null;
  } catch {
    return false;
  }
}

/**
 * Records that the nudge has been answered on this browser profile — permanent, so an answered banner
 * never returns (design doc §10). A blocked write degrades silently: the caller (`PasskeyNudgeBanner`)
 * still hides itself via its own state for the rest of this document, it simply may show again on the
 * next load, the same "unanswered" fallback the trigger already tolerates.
 */
export function markNudgeAnswered(): void {
  try {
    localStorage.setItem(PASSKEY_NUDGE_KEY, JSON.stringify({ answeredAt: Date.now() }));
  } catch {
    // Storage blocked — see doc comment above.
  }
}
